import { formatDateTimeShort } from "@/shared/lib/date-time";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import type { CartItemSnapshot } from "../../domain/contracts/offline-sync";
import { posApi } from "../api/client";
import { buildLocalDiscountsHtml } from "./offline-sync/local-pricing-snapshot";
import {
  getCachedKitchenRouting,
  getCachedReferenceData,
  saveReferenceCacheSection,
} from "./offline-sync/reference-cache";
import {
  groupItemsByStation,
  type KitchenRoutingEntry,
} from "./offline-sync/kitchen-routing";
import type { LocalOrderRecord } from "./offline-sync/types";
import {
  formatBillAmount,
  formatDiscountLabelForBill,
} from "./bill-print-format";
import { resolveLocalMemberReceipt, getMemberReceiptTotalBalance } from "./member-receipt";
import { printHtmlDocument } from "./print-html";

const SHARED_STYLES = `
  html, body { width: 72.1mm; max-width: 72.1mm; min-width: 72.1mm; margin: 0 auto; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, sans-serif; color: #000; background: #fff; font-size: 16px; line-height: 1.5; font-weight: 500; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  *, *::before, *::after { box-sizing: border-box; }
  .print-page { page-break-after: always; }
  .print-page:last-child { page-break-after: auto; }
  .bill-header { text-align: center; margin-bottom: 8px; }
  .bill-header .shop-name { font-size: 22px; font-weight: bold; letter-spacing: 0.5px; }
  .bill-header .bill-title { font-size: 15px; color: #000; margin-top: 2px; font-weight: 500; }
  .divider { border: none; border-top: 0.5px dashed #000; margin: 8px 0; }
  .order-info { text-align: start; font-size: 15px; color: #000; margin-bottom: 10px; line-height: 1.5; font-weight: 500; }
  .items-table { width: 100%; border-collapse: collapse; font-size: 15px; font-weight: 500; }
  .items-table td { padding: 5px 0; vertical-align: top; border-bottom: 1px dotted #000; }
  .items-table .item-name { font-weight: bold; font-size: 16px; }
  .items-table .item-mod { font-size: 14px; color: #000; margin-left: 4px; font-weight: 500; }
  .items-table .item-detail { font-size: 14px; color: #000; font-weight: 500; }
  .items-table .item-price { text-align: right; white-space: nowrap; font-size: 15px; font-weight: 500; }
  .summary-table { width: 100%; border-collapse: collapse; font-size: 15px; margin-top: 8px; font-weight: 500; }
  .summary-table td { padding: 4px 0; }
  .summary-table .summary-label { color: #000; }
  .summary-table .summary-value { text-align: right; white-space: nowrap; }
  .summary-table .discount-value { color: #000; }
  .grand-total { font-weight: bold; font-size: 18px; border-top: 2px solid #000; padding-top: 8px; margin-top: 4px; }
  .grand-total td { padding-top: 8px !important; }
  .bill-footer { text-align: center; margin-top: 16px; font-size: 15px; color: #000; font-weight: 500; }
  .bill-footer .bill-footer-date { font-size: 13px; margin-bottom: 12px; }
  .bill-footer .thanks { margin-bottom: 6px; }
  .bill-footer .thanks-en { font-size: 14px; }
  .bill-logo { max-width: 48mm; max-height: 24mm; margin: 0 auto 6px; display: block; }
  .bill-contact { font-size: 13px; color: #000; margin-top: 4px; }
  .member-info { font-size: 14px; color: #000; margin-top: 8px; line-height: 1.6; font-weight: 500; text-align: start; }
  .member-info-title { font-weight: bold; font-size: 15px; margin-bottom: 2px; }
  @media print {
    @page { size: 72.1mm auto; margin: 0; }
    html, body { width: 72.1mm !important; max-width: 72.1mm !important; min-width: 72.1mm !important; margin: 0 !important; padding: 4mm !important; font-weight: 500 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body, .order-info, .items-table, .items-table td, .items-table .item-mod, .items-table .item-detail, .items-table .item-price, .summary-table, .summary-table td, .bill-footer { font-weight: 500 !important; }
    .bill-header .shop-name, .items-table .item-name, .grand-total { font-weight: bold !important; }
  }
`;

const WAIT_AND_PRINT_SCRIPT = `
<script>
(function(){
  var done = false;
  function doPrint() {
    if (done) return;
    done = true;
    window.addEventListener('afterprint', function(){ window.close(); }, { once: true });
    setTimeout(function(){ window.print(); }, 150);
  }
  var imgs = document.querySelectorAll('img');
  var needWait = 0;
  for (var i = 0; i < imgs.length; i++) {
    if (!imgs[i].complete) needWait++;
  }
  var fallback = setTimeout(doPrint, 3000);
  function check() {
    needWait--;
    if (needWait <= 0) {
      clearTimeout(fallback);
      doPrint();
    }
  }
  if (needWait === 0) {
    clearTimeout(fallback);
    doPrint();
    return;
  }
  for (var i = 0; i < imgs.length; i++) {
    if (!imgs[i].complete) {
      imgs[i].onload = check;
      imgs[i].onerror = check;
    }
  }
})();
</script>`;

/** บิล: แสดงเฉพาะค่าที่เลือก — ไม่แสดงชื่อกลุ่ม (optionName / label) */
function formatBillVariantOptionText(opt: {
  optionName?: string | null;
  optionValue?: string | null;
}): string {
  const v = opt.optionValue?.trim();
  if (v) return v;
  return (opt.optionName ?? "").trim() || "";
}

function formatBillModifierText(m: {
  label: string;
  optionLabel?: string | null;
}): string {
  const opt = m.optionLabel?.trim();
  if (opt) return opt;
  return m.label;
}

function getOrderTypeLabel(orderType: string): string {
  switch (orderType) {
    case "DINE_IN":
      return "Dine In";
    case "TAKEAWAY":
      return "Take Away";
    case "DELIVERY":
      return "Delivery";
    default:
      return orderType;
  }
}

function buildMemberReceiptHtml(
  memberReceipt?: {
    phone: string | null;
    firstName?: string | null;
    lastName?: string | null;
    loyaltyPointsBalance: number;
    loyaltyPointsEarned?: number;
    loyaltyPointsRedeemed?: number;
    balanceIncludesEarned?: boolean;
  } | null,
): string {
  if (!memberReceipt) return "";

  const nameParts = [memberReceipt.firstName, memberReceipt.lastName]
    .map((part) => part?.trim())
    .filter(Boolean);
  const nameLine =
    nameParts.length > 0
      ? `<div>Member: ${nameParts.join(" ")}</div>`
      : "";
  const phoneLine = memberReceipt.phone?.trim()
    ? `<div>Phone: ${memberReceipt.phone.trim()}</div>`
    : "";
  const earned = memberReceipt.loyaltyPointsEarned ?? 0;
  const redeemed = memberReceipt.loyaltyPointsRedeemed ?? 0;
  const totalBalance = getMemberReceiptTotalBalance(memberReceipt);
  const earnedLine =
    earned > 0
      ? `<div>Points earned: +${formatBillAmount(earned)}</div>`
      : "";
  const redeemedLine =
    redeemed > 0
      ? `<div>Reward redeemed: −${formatBillAmount(redeemed)} pts</div>`
      : "";

  return `
    <div class="member-info">
      ${nameLine}
      ${phoneLine}
      ${redeemedLine}
      ${earnedLine}
      <div>Points balance: ${formatBillAmount(totalBalance)}</div>
    </div>`;
}

function openPrintWindow(combinedHtml: string): void {
  printHtmlDocument(combinedHtml);
}

/** Type for print data; supplementTotal set only for Supplementary Bill. Defined explicitly to avoid circular ref with fetchPrintData. */
type FetchedPrintData = Awaited<ReturnType<typeof fetchPrintData>>;
type BranchReceiptInfo = Awaited<
  ReturnType<typeof posApi.getBranchReceiptInfo>
>;
type CachedBranchReceiptInfo = BranchReceiptInfo & {
  billSettings: BranchReceiptInfo["billSettings"] & {
    logoDataUrl?: string | null;
  };
};

const SUPPLEMENTARY_KITCHEN_TICKETS_WINDOW_MS = 2 * 60 * 1000; // 2 minutes

function canUseNetwork(): boolean {
  return typeof navigator === "undefined" || navigator.onLine;
}

async function loadLogoDataUrl(logoUrl: string | null): Promise<string> {
  if (!logoUrl) return "";
  const resolvedUrl = resolveImageSrc(logoUrl);
  if (!resolvedUrl) return "";

  try {
    const res = await fetch(resolvedUrl, {
      credentials: "include",
      signal: AbortSignal.timeout(8_000),
    });
    if (!res.ok) return resolvedUrl;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return resolvedUrl;
  }
}

async function cacheReceiptLogoDataUrl(
  receiptInfo: BranchReceiptInfo,
): Promise<CachedBranchReceiptInfo> {
  const logoDataUrl = await loadLogoDataUrl(receiptInfo.billSettings.logoUrl);
  return {
    ...receiptInfo,
    billSettings: {
      ...receiptInfo.billSettings,
      logoDataUrl: logoDataUrl || null,
    },
  };
}

async function buildReceiptPresentation(receiptInfo: CachedBranchReceiptInfo) {
  const brandName =
    receiptInfo.billSettings.brandName || receiptInfo.branch.name;
  const addressParts = [
    receiptInfo.branch.addressLine1,
    receiptInfo.branch.addressLine2,
    receiptInfo.branch.district,
    receiptInfo.branch.province,
  ].filter(Boolean);
  const addressLine = addressParts.join(" ");

  const logoSrc =
    receiptInfo.billSettings.logoDataUrl ||
    (await loadLogoDataUrl(receiptInfo.billSettings.logoUrl));

  const logoHtml = logoSrc
    ? `<img src="${logoSrc}" alt="" class="bill-logo" />`
    : "";
  const contactHtml = [
    addressLine ? `<span>${addressLine}</span>` : "",
    receiptInfo.billSettings.phone
      ? `<span>Tel: ${receiptInfo.billSettings.phone}</span>`
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return {
    brandName,
    logoHtml,
    contactHtml,
    showTax: receiptInfo.billSettings.showTaxOnBill,
  };
}

type PaymentMethodCacheItem = {
  id: string;
  displayName: string;
  code?: string;
};

async function getPaymentMethodNameById(
  branchId: string,
  deviceId?: string,
): Promise<Map<string, string>> {
  let methods = await getCachedReferenceData<PaymentMethodCacheItem[]>(
    branchId,
    "payment-methods",
  );

  if ((!methods || methods.length === 0) && canUseNetwork()) {
    try {
      methods = await posApi.getPaymentMethods(branchId);
      await saveReferenceCacheSection(branchId, "payment-methods", methods, {
        deviceId,
      });
    } catch (error) {
      console.warn("[print-order-bill] payment methods fetch failed", {
        branchId,
        error,
      });
      methods = [];
    }
  }

  return new Map(
    (methods ?? []).map((method) => [
      method.id,
      method.displayName?.trim() || method.code || "Payment",
    ]),
  );
}

function resolvePaymentDisplayName(
  methodId: string,
  paymentMethodNames: Map<string, string>,
): string {
  return paymentMethodNames.get(methodId) ?? "Payment";
}

async function getLocalReceiptInfo(
  order: LocalOrderRecord,
): Promise<CachedBranchReceiptInfo> {
  const cached = await getCachedReferenceData<CachedBranchReceiptInfo>(
    order.branchId,
    "branch-receipt-info",
  );
  if (cached) return cached;

  if (canUseNetwork()) {
    try {
      const receiptInfo = await cacheReceiptLogoDataUrl(
        await posApi.getBranchReceiptInfo(order.branchId),
      );
      await saveReferenceCacheSection(
        order.branchId,
        "branch-receipt-info",
        receiptInfo,
        { deviceId: order.deviceId },
      );
      return receiptInfo;
    } catch (error) {
      console.warn("[print-order-bill] receipt info fetch failed", {
        branchId: order.branchId,
        error,
      });
    }
  }

  return {
    branch: {
      name: order.selection.branchName,
      addressLine1: null,
      addressLine2: null,
      district: null,
      province: null,
      postalCode: null,
    },
    billSettings: {
      logoUrl: null,
      brandName: order.selection.branchName,
      phone: null,
      footerThankYouLao: null,
      footerThankYouEnglish: "Thank you for your visit",
      showTaxOnBill: true,
      logoDataUrl: null,
    },
  };
}

async function fetchPrintData(
  orderId: string,
  branchId: string,
  options?: { newOrderItemIds?: string[] },
) {
  const [orderDetailRes, ticketsResponse, receiptInfoRes, paymentsRes] =
    await Promise.all([
      posApi.getOrderDetail(orderId, branchId),
      posApi.getKitchenTickets(branchId, { orderId }, { limit: 20, offset: 0 }),
      posApi.getBranchReceiptInfo(branchId),
      posApi.getOrderPayments(orderId, branchId),
    ]);

  const orderDetail =
    typeof (orderDetailRes as { data?: unknown }).data !== "undefined"
      ? (orderDetailRes as unknown as { data: typeof orderDetailRes }).data
      : orderDetailRes;
  const receiptInfo =
    typeof (receiptInfoRes as { data?: unknown }).data !== "undefined"
      ? (receiptInfoRes as unknown as { data: typeof receiptInfoRes }).data
      : receiptInfoRes;
  const enrichedReceiptInfo = await cacheReceiptLogoDataUrl(receiptInfo);

  const { order, items, tableName, salespersonName } = orderDetail;
  const memberReceipt =
    (
      orderDetail as typeof orderDetail & {
        memberReceipt?: {
          phone: string | null;
          firstName?: string | null;
          lastName?: string | null;
          loyaltyPointsBalance: number;
          loyaltyPointsEarned?: number;
        } | null;
      }
    ).memberReceipt ?? null;
  const discounts =
    (
      orderDetail as typeof orderDetail & {
        discounts?: Array<{ label: string; amount: number | string }>;
      }
    ).discounts ?? [];
  let kitchenTickets = ticketsResponse.data ?? [];
  const payments = Array.isArray(paymentsRes)
    ? paymentsRes
    : ((paymentsRes as { data?: unknown[] })?.data ?? []);

  /** รายการที่ยังไม่ถูกยกเลิก — ใช้ใน receipt/proforma เท่านั้น */
  let activeItems = items.filter(
    (i) => !(i as { voidedAt?: string | Date | null }).voidedAt,
  );
  /** กรณี Supplementary: แสดงเฉพาะรายการที่สั่งเพิ่ม */
  const newOrderItemIdsSet =
    options?.newOrderItemIds && options.newOrderItemIds.length > 0
      ? new Set(options.newOrderItemIds)
      : null;
  if (newOrderItemIdsSet) {
    activeItems = activeItems.filter((i) => newOrderItemIdsSet.has(i.id));
    const since = Date.now() - SUPPLEMENTARY_KITCHEN_TICKETS_WINDOW_MS;
    kitchenTickets = kitchenTickets.filter((kt) => {
      const raw = (kt as unknown as { ticket?: { queuedAt?: string | Date } })
        .ticket?.queuedAt;
      const queuedAt =
        raw instanceof Date
          ? raw.getTime()
          : typeof raw === "string"
            ? new Date(raw).getTime()
            : 0;
      return queuedAt >= since;
    });
  }
  /** ID ของ order item ที่ถูก void — ใช้กรอง kitchen ticket items */
  const voidedOrderItemIds = new Set(
    items
      .filter((i) => (i as { voidedAt?: string | Date | null }).voidedAt)
      .map((i) => i.id),
  );

  const paidTotal = Number(order.paidTotal) || 0;
  const grandTotal =
    Number(order.subtotal) +
    Number(order.serviceChargeTotal) +
    Number(order.taxTotal) -
    Number(order.discountTotal);
  const { brandName, logoHtml, contactHtml, showTax } =
    await buildReceiptPresentation(enrichedReceiptInfo);

  /** ยอดรวมของรายการเสริมเท่านั้น (ใช้ใน Supplementary Bill) */
  const supplementTotal =
    newOrderItemIdsSet && activeItems.length > 0
      ? activeItems.reduce(
          (sum, item) =>
            sum + Number(item.lineSubtotal) + Number(item.taxTotal || 0),
          0,
        )
      : undefined;

  const itemsHtml = activeItems
    .map((item) => {
      const qty = Number(item.quantity);
      const lineSubExcl = Number(item.lineSubtotal);
      const itemTax = Number(item.taxTotal) || 0;
      const lineSubIncl = lineSubExcl + itemTax;
      const unitPrice = showTax
        ? Number(item.unitPrice)
        : lineSubIncl / (qty || 1);
      const lineSubtotal = showTax ? lineSubExcl : lineSubIncl;
      const variantOptParts =
        item.variantOptions
          ?.map((opt) => formatBillVariantOptionText(opt))
          .filter(Boolean) ?? [];
      const variantOpts =
        variantOptParts.length > 0
          ? `<div class="item-mod">${variantOptParts.join(", ")}</div>`
          : "";
      const mods =
        item.modifiers && item.modifiers.length > 0
          ? `<div class="item-mod">+ ${item.modifiers.map((m) => formatBillModifierText(m as { label: string; optionLabel?: string | null })).join(", ")}</div>`
          : "";
      return `
      <tr>
        <td>
          <div class="item-name">${item.productName}</div>
          ${variantOpts}
          ${mods}
          <div class="item-detail">${formatBillAmount(qty)} x ${formatBillAmount(unitPrice)} ₭</div>
        </td>
        <td class="item-price">${formatBillAmount(lineSubtotal)} ₭</td>
      </tr>`;
    })
    .join("");

  const discountsHtml =
    discounts.length > 0
      ? discounts
          .map(
            (d) =>
              `<tr><td class="summary-label">${formatDiscountLabelForBill(d.label)}</td><td class="summary-value discount-value">-${formatBillAmount(Math.abs(Number(d.amount)))} ₭</td></tr>`,
          )
          .join("")
      : Number(order.discountTotal) > 0
        ? `<tr><td class="summary-label">Discount</td><td class="summary-value discount-value">-${formatBillAmount(Number(order.discountTotal))} ₭</td></tr>`
        : "";

  const tableInfo =
    order.orderType === "DINE_IN" && tableName ? `<br>Table: ${tableName}` : "";
  const salespersonInfo = salespersonName
    ? `<br>Salesperson: ${salespersonName}`
    : "";

  return {
    order,
    itemsHtml,
    discountsHtml,
    showTax,
    grandTotal,
    brandName,
    logoHtml,
    contactHtml,
    tableInfo,
    salespersonInfo,
    receiptInfo: enrichedReceiptInfo,
    kitchenTickets,
    paidTotal,
    voidedOrderItemIds,
    payments,
    memberReceipt,
    supplementTotal,
    /** เฉพาะกรณี Supplementary: Set ของ orderItemId ที่อยู่ในใบเสริม — ใช้กรอง kitchen ticket items */
    supplementOrderItemIds: newOrderItemIdsSet ?? undefined,
  };
}

function buildReceiptPageHtml(
  d: FetchedPrintData,
  options: { billTitle: string; includePaymentAndChange: boolean },
): string {
  const {
    order,
    itemsHtml,
    discountsHtml,
    showTax,
    grandTotal,
    brandName,
    logoHtml,
    contactHtml,
    tableInfo,
    salespersonInfo,
    receiptInfo,
    paidTotal,
    payments,
    memberReceipt,
  } = d;

  const paymentRows =
    options.includePaymentAndChange &&
    order.status === "PAID" &&
    (payments?.length ?? 0) > 0
      ? (
          payments as Array<{
            method?: { displayName?: string };
            amountGross?: number;
          }>
        )
          .map(
            (p) =>
              `<tr><td class="summary-label">${p.method?.displayName ?? "Payment"}</td><td class="summary-value">${formatBillAmount(Number(p.amountGross ?? 0))} ₭</td></tr>`,
          )
          .join("")
      : "";
  const paidInfo =
    options.includePaymentAndChange && order.status === "PAID" && paidTotal > 0
      ? `<tr><td class="summary-label">Total Paid</td><td class="summary-value">${formatBillAmount(paidTotal)} ₭</td></tr>`
      : "";
  const changeAmount =
    options.includePaymentAndChange &&
    order.status === "PAID" &&
    paidTotal > grandTotal
      ? paidTotal - grandTotal
      : 0;
  const changeInfo =
    options.includePaymentAndChange && changeAmount > 0
      ? `<tr><td class="summary-label">Change</td><td class="summary-value">${formatBillAmount(changeAmount)} ₭</td></tr>`
      : "";
  const memberInfoHtml = options.includePaymentAndChange
    ? buildMemberReceiptHtml(memberReceipt)
    : "";

  return `
  <div class="print-page">
    <div class="bill-header">
      ${logoHtml}
      <div class="shop-name">${brandName}</div>
      ${contactHtml ? `<div class="bill-contact">${contactHtml}</div>` : ""}
      <div class="bill-title">${options.billTitle}</div>
    </div>
    <hr class="divider">
    <div class="order-info">
      Bill No. #${order.orderNo}<br>
      ${getOrderTypeLabel(order.orderType)}${tableInfo}${salespersonInfo}
    </div>
    <hr class="divider">
    <table class="items-table">
      ${itemsHtml}
    </table>
   ${Number(order.subtotal) > 0 || Number(order.taxTotal) > 0 || Number(order.serviceChargeTotal) > 0 ? `<table class="summary-table">` : ""}
      ${showTax && Number(order.taxTotal) > 0 ? `<tr><td class="summary-label">Tax</td><td class="summary-value">${formatBillAmount(Number(order.taxTotal))} ₭</td></tr>` : ""}
      ${showTax && (Number(order.subtotal) > 0 || Number(order.taxTotal) > 0) ? `<tr><td class="summary-label">Subtotal (incl. tax)</td><td class="summary-value">${formatBillAmount(Number(order.subtotal) + Number(order.taxTotal))} ₭</td></tr>` : ""}
      ${Number(order.serviceChargeTotal) > 0 ? `<tr><td class="summary-label">Service Charge</td><td class="summary-value">${formatBillAmount(Number(order.serviceChargeTotal))} ₭</td></tr>` : ""}
      ${discountsHtml}
      <tr class="grand-total"><td>Grand Total</td><td class="summary-value">${formatBillAmount(grandTotal)} ₭</td></tr>
      ${paymentRows}
      ${paidInfo}
      ${changeInfo}
    </table>
    ${memberInfoHtml ? `<hr class="divider">${memberInfoHtml}` : ""}
    <hr class="divider">
    <div class="bill-footer">
      <div class="bill-footer-date">${formatDateTimeShort(order.createdAt)}</div>
      <div class="thanks-en">${receiptInfo.billSettings.footerThankYouEnglish?.trim() || "Thank you for your visit"}</div>
    </div>
  </div>`;
}

function buildSupplementaryBillPageHtml(d: FetchedPrintData): string {
  const {
    order,
    itemsHtml,
    brandName,
    logoHtml,
    contactHtml,
    tableInfo,
    salespersonInfo,
    receiptInfo,
    supplementTotal,
  } = d;
  const total =
    supplementTotal !== undefined && supplementTotal !== null
      ? supplementTotal
      : 0;
  return `
  <div class="print-page">
    <div class="bill-header">
      ${logoHtml}
      <div class="shop-name">${brandName}</div>
      ${contactHtml ? `<div class="bill-contact">${contactHtml}</div>` : ""}
      <div class="bill-title">SUPPLEMENTARY BILL</div>
    </div>
    <hr class="divider">
    <div class="order-info">
      Bill No. #${order.orderNo}<br>
      ${getOrderTypeLabel(order.orderType)}${tableInfo}${salespersonInfo}
    </div>
    <hr class="divider">
    <table class="items-table">
      ${itemsHtml}
    </table>
    <table class="summary-table">
      <tr class="grand-total"><td>Supplement Total</td><td class="summary-value">${formatBillAmount(total)} ₭</td></tr>
    </table>
    <hr class="divider">
    <div class="bill-footer">
      <div class="bill-footer-date">${formatDateTimeShort(order.updatedAt ?? order.createdAt)}</div>
      <div class="thanks-en">${receiptInfo.billSettings.footerThankYouEnglish?.trim() || "Thank you for your visit"}</div>
    </div>
  </div>`;
}

function buildKitchenTicketsHtml(d: FetchedPrintData): string {
  const {
    kitchenTickets,
    brandName,
    voidedOrderItemIds,
    supplementOrderItemIds,
  } = d;
  return kitchenTickets
    .map((kt) => {
      let activeTicketItems = (kt.items ?? []).filter(
        (item: { orderItemId: string }) =>
          !voidedOrderItemIds.has(item.orderItemId),
      );
      // กรณี Supplementary: แสดงเฉพาะรายการที่อยู่ในใบเสริม
      if (supplementOrderItemIds && supplementOrderItemIds.size > 0) {
        activeTicketItems = activeTicketItems.filter(
          (item: { orderItemId: string }) =>
            supplementOrderItemIds.has(item.orderItemId),
        );
      }
      if (activeTicketItems.length === 0) return "";
      const ticketItemsHtml = activeTicketItems
        .map((item) => {
          const variantOptParts =
            item.variantOptions
              ?.map((opt) => formatBillVariantOptionText(opt))
              .filter(Boolean) ?? [];
          const variantOpts =
            variantOptParts.length > 0
              ? `<div class="item-mod">${variantOptParts.join(", ")}</div>`
              : "";
          const mods =
            item.modifiers && item.modifiers.length > 0
              ? `<div class="item-mod">+ ${item.modifiers.map((m) => formatBillModifierText(m)).join(", ")}</div>`
              : "";
          return `
            <tr>
              <td>
                <div class="item-name">${item.displayName}</div>
                ${variantOpts}
                ${mods}
                ${item.note ? `<div class="item-mod">${item.note}</div>` : ""}
                <div class="item-detail">${formatBillAmount(Number(item.quantity))} x</div>
              </td>
            </tr>`;
        })
        .join("");
      const ktTableInfo = kt.order.tableName
        ? ` · Table ${kt.order.tableName}`
        : "";
      return `
  <div class="print-page">
    <div class="bill-header">
      <div class="shop-name">${brandName}</div>
      <div class="bill-title">KITCHEN ORDER</div>
    </div>
    <hr class="divider">
    <div class="order-info">
      ${kt.station.name} · Order #${kt.order.orderNo}${ktTableInfo}<br>
      ${formatDateTimeShort(kt.ticket.queuedAt)}
    </div>
    <hr class="divider">
    <table class="items-table">
      ${ticketItemsHtml}
    </table>
  </div>`;
    })
    .join("");
}

function buildLocalItemsHtml(items: CartItemSnapshot[]): string {
  return items
    .map((item) => {
      const qty = Number(item.quantity);
      const unitPrice = item.totalPrice / (qty || 1);
      const modifiers =
        item.modifiers.length > 0
          ? `<div class="item-mod">+ ${item.modifiers.map((m) => m.optionLabel || m.modifierName).join(", ")}</div>`
          : "";
      const note = item.note ? `<div class="item-mod">${item.note}</div>` : "";
      const variantOpts = item.variantName
        ? `<div class="item-mod">${item.variantName}</div>`
        : "";
      return `
        <tr>
          <td>
            <div class="item-name">${item.productName}</div>
            ${variantOpts}
            ${modifiers}
            ${note}
            <div class="item-detail">${formatBillAmount(qty)} x ${formatBillAmount(unitPrice)} ₭</div>
          </td>
          <td class="item-price">${formatBillAmount(item.totalPrice)} ₭</td>
        </tr>`;
    })
    .join("");
}

function buildLocalKitchenTickets(
  order: LocalOrderRecord,
  items: CartItemSnapshot[],
  routingEntries: KitchenRoutingEntry[],
) {
  if (items.length === 0) return [];

  const queuedAt = new Date().toISOString();
  const stationGroups = groupItemsByStation(items, routingEntries);

  return stationGroups.map((group) => ({
    station: { name: group.stationName },
    order: {
      orderNo: `L${order.clientOrderNo}`,
      tableName: order.selection.tableName ?? null,
    },
    ticket: {
      queuedAt,
    },
    items: group.items.map((item) => ({
      orderItemId: item.id,
      displayName: item.productName,
      quantity: item.quantity,
      note: item.note,
      variantOptions: item.variantName
        ? [{ optionName: "Variant", optionValue: item.variantName }]
        : [],
      modifiers: item.modifiers.map((modifier) => ({
        label: modifier.modifierName,
        optionLabel: modifier.optionLabel,
      })),
    })),
  }));
}

async function buildLocalPrintData(
  order: LocalOrderRecord,
  options: { items?: CartItemSnapshot[]; supplementary?: boolean } = {},
): Promise<FetchedPrintData> {
  const items = options.items ?? order.cartItems;
  const orderType = order.isDelivery
    ? "DELIVERY"
    : order.selection.tableId
      ? "DINE_IN"
      : "TAKEAWAY";
  const supplementTotal = options.supplementary
    ? items.reduce((sum, item) => sum + item.totalPrice, 0)
    : undefined;
  const receiptInfo = await getLocalReceiptInfo(order);
  const { brandName, logoHtml, contactHtml, showTax } =
    await buildReceiptPresentation(receiptInfo);
  const paymentMethodNames = await getPaymentMethodNameById(
    order.branchId,
    order.deviceId,
  );
  const kitchenRouting = await getCachedKitchenRouting(order.branchId);

  return {
    order: {
      orderNo: `L${order.clientOrderNo}`,
      orderType,
      status: order.status,
      subtotal: order.totals.subtotal,
      serviceChargeTotal: order.totals.serviceChargeTotal,
      taxTotal: order.totals.taxTotal,
      discountTotal: order.totals.discountTotal,
      paidTotal: order.payments.reduce(
        (sum, payment) => sum + payment.amountGross,
        0,
      ),
      createdAt: order.createdAt,
      updatedAt: order.updatedAt,
    },
    itemsHtml: buildLocalItemsHtml(items),
    discountsHtml: buildLocalDiscountsHtml(order),
    showTax,
    grandTotal: options.supplementary
      ? (supplementTotal ?? 0)
      : order.totals.grandTotal,
    brandName,
    logoHtml,
    contactHtml,
    tableInfo: order.selection.tableName
      ? `<br>Table: ${order.selection.tableName}`
      : "",
    salespersonInfo: order.selection.salespersonName
      ? `<br>Salesperson: ${order.selection.salespersonName}`
      : "",
    receiptInfo,
    kitchenTickets: buildLocalKitchenTickets(order, items, kitchenRouting),
    paidTotal: order.payments.reduce(
      (sum, payment) => sum + payment.amountGross,
      0,
    ),
    voidedOrderItemIds: new Set(),
    payments: order.payments.map((payment) => ({
      method: {
        displayName: resolvePaymentDisplayName(
          payment.methodId,
          paymentMethodNames,
        ),
      },
      amountGross: payment.amountGross,
    })),
    memberReceipt: await resolveLocalMemberReceipt(order),
    supplementTotal,
    supplementOrderItemIds: options.supplementary
      ? new Set(items.map((item) => item.id))
      : undefined,
  } as unknown as FetchedPrintData;
}

function buildLocalAdjustmentNotePageHtml(
  d: FetchedPrintData,
  options: {
    title: string;
    lines: string[];
    itemsHtml?: string;
  },
): string {
  const linesHtml = options.lines
    .filter((line) => line.trim().length > 0)
    .map((line) => `<div>${line}</div>`)
    .join("");
  const itemsSection = options.itemsHtml
    ? `<hr class="divider"><table class="items-table">${options.itemsHtml}</table>`
    : "";

  return `
  <div class="print-page">
    <div class="bill-header">
      ${d.logoHtml}
      <div class="shop-name">${d.brandName}</div>
      ${d.contactHtml ? `<div class="bill-contact">${d.contactHtml}</div>` : ""}
      <div class="bill-title">${options.title}</div>
    </div>
    <hr class="divider">
    <div class="order-info">
      ${linesHtml}
    </div>
    ${itemsSection}
  </div>`;
}

function buildLocalKitchenNoteItemsHtml(items: CartItemSnapshot[]): string {
  return items
    .map(
      (item) => `
      <tr>
        <td>
          <div class="item-name">${item.productName}</div>
          ${item.variantName ? `<div class="item-mod">${item.variantName}</div>` : ""}
          ${item.modifiers.length > 0 ? `<div class="item-mod">+ ${item.modifiers.map((modifier) => modifier.optionLabel || modifier.modifierName).join(", ")}</div>` : ""}
          ${item.note ? `<div class="item-mod">${item.note}</div>` : ""}
          <div class="item-detail">${formatBillAmount(item.quantity)} x</div>
        </td>
      </tr>`,
    )
    .join("");
}

export async function printLocalProformaAndKitchen(
  order: LocalOrderRecord,
): Promise<void> {
  const d = await buildLocalPrintData(order);
  const proformaHtml = buildReceiptPageHtml(d, {
    billTitle: "BILL PROFORMA",
    includePaymentAndChange: false,
  });
  const kitchenHtml = buildKitchenTicketsHtml(d);
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Proforma Order #L${order.clientOrderNo}</title><style>${SHARED_STYLES}</style></head><body>${proformaHtml}${kitchenHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

export async function printLocalProformaOnly(
  order: LocalOrderRecord,
): Promise<void> {
  const d = await buildLocalPrintData(order);
  const proformaHtml = buildReceiptPageHtml(d, {
    billTitle: "BILL PROFORMA",
    includePaymentAndChange: false,
  });
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Proforma Order #L${order.clientOrderNo}</title><style>${SHARED_STYLES}</style></head><body>${proformaHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

export async function printLocalReceiptAndKitchen(
  order: LocalOrderRecord,
): Promise<void> {
  const d = await buildLocalPrintData(order);
  const receiptHtml = buildReceiptPageHtml(d, {
    billTitle: "RECEIPT",
    includePaymentAndChange: true,
  });
  const kitchenHtml = buildKitchenTicketsHtml(d);
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt Order #L${order.clientOrderNo}</title><style>${SHARED_STYLES}</style></head><body>${receiptHtml}${kitchenHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

export async function printLocalReceiptOnly(
  order: LocalOrderRecord,
): Promise<void> {
  const d = await buildLocalPrintData(order);
  const receiptHtml = buildReceiptPageHtml(d, {
    billTitle: "RECEIPT",
    includePaymentAndChange: true,
  });
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt Order #L${order.clientOrderNo}</title><style>${SHARED_STYLES}</style></head><body>${receiptHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

export async function printLocalSupplementaryBillAndKitchen(
  order: LocalOrderRecord,
  addedItems: CartItemSnapshot[],
): Promise<void> {
  if (addedItems.length === 0) return;
  const d = await buildLocalPrintData(order, {
    items: addedItems,
    supplementary: true,
  });
  const supplementaryHtml = buildSupplementaryBillPageHtml(d);
  const kitchenHtml = buildKitchenTicketsHtml(d);
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Supplementary Order #L${order.clientOrderNo}</title><style>${SHARED_STYLES}</style></head><body>${supplementaryHtml}${kitchenHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

export async function printLocalKitchenOnly(
  order: LocalOrderRecord,
): Promise<void> {
  const d = await buildLocalPrintData(order);
  const kitchenHtml = buildKitchenTicketsHtml(d);
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Kitchen Order #L${order.clientOrderNo}</title><style>${SHARED_STYLES}</style></head><body>${kitchenHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

export async function printLocalCancelKitchenNote(
  order: LocalOrderRecord,
): Promise<void> {
  const d = await buildLocalPrintData(order);
  const cancelHtml = buildLocalAdjustmentNotePageHtml(d, {
    title: "CANCEL KITCHEN NOTE",
    lines: [
      `Order #L${order.clientOrderNo}`,
      order.selection.tableName ? `Table: ${order.selection.tableName}` : "",
      formatDateTimeShort(order.voidedAt ?? order.updatedAt),
      order.voidReason ? `Reason: ${order.voidReason}` : "",
    ],
    itemsHtml: buildLocalKitchenNoteItemsHtml(order.cartItems),
  });
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Cancel Kitchen Order #L${order.clientOrderNo}</title><style>${SHARED_STYLES}</style></head><body>${cancelHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

export async function printLocalMoveTableNote(
  order: LocalOrderRecord,
  options: {
    fromTableName?: string | null;
    toTableName?: string | null;
    movedAt?: string;
    reason?: string | null;
  },
): Promise<void> {
  const d = await buildLocalPrintData(order);
  const moveHtml = buildLocalAdjustmentNotePageHtml(d, {
    title: "MOVE TABLE NOTE",
    lines: [
      `Order #L${order.clientOrderNo}`,
      `From: ${options.fromTableName || "No table"}`,
      `To: ${options.toTableName || "No table"}`,
      formatDateTimeShort(options.movedAt ?? order.updatedAt),
      options.reason ? `Reason: ${options.reason}` : "",
    ],
  });
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Move Table Order #L${order.clientOrderNo}</title><style>${SHARED_STYLES}</style></head><body>${moveHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

export async function printLocalMergeOrderNote(
  targetOrder: LocalOrderRecord,
  sourceOrders: LocalOrderRecord[],
  options: {
    mergedAt?: string;
    reason?: string | null;
  } = {},
): Promise<void> {
  const d = await buildLocalPrintData(targetOrder);
  const sourceLines = sourceOrders.map((source) => {
    const table = source.selection.tableName
      ? ` / Table: ${source.selection.tableName}`
      : "";
    return `Source: #L${source.clientOrderNo}${table}`;
  });
  const targetTable = targetOrder.selection.tableName
    ? ` / Table: ${targetOrder.selection.tableName}`
    : "";
  const mergeHtml = buildLocalAdjustmentNotePageHtml(d, {
    title: "MERGE ORDER NOTE",
    lines: [
      ...sourceLines,
      `Target: #L${targetOrder.clientOrderNo}${targetTable}`,
      formatDateTimeShort(options.mergedAt ?? targetOrder.updatedAt),
      options.reason ? `Reason: ${options.reason}` : "",
    ],
  });
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Merge Order #L${targetOrder.clientOrderNo}</title><style>${SHARED_STYLES}</style></head><body>${mergeHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

/**
 * พิมพ์ทั้งใบเสร็จ (RECEIPT) และ kitchen tickets — ใช้เมื่อจ่ายเงินจากหน้าสรุป Order
 */
export async function printOrderBill(
  orderId: string,
  branchId: string,
): Promise<void> {
  const d = await fetchPrintData(orderId, branchId);
  const receiptHtml = buildReceiptPageHtml(d, {
    billTitle: "RECEIPT",
    includePaymentAndChange: true,
  });
  const kitchenHtml = buildKitchenTicketsHtml(d);
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Bill Order #${d.order.orderNo}</title><style>${SHARED_STYLES}</style></head><body>${receiptHtml}${kitchenHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

/**
 * พิมพ์ใบแจ้งหนี้ (PROFORMA) + kitchen tickets — ใช้เมื่อสั่งก่อนจ่ายทีหลัง (ຢືນຢັນ Order)
 */
export async function printProformaAndKitchen(
  orderId: string,
  branchId: string,
): Promise<void> {
  const d = await fetchPrintData(orderId, branchId);
  const proformaHtml = buildReceiptPageHtml(d, {
    billTitle: "BILL PROFORMA",
    includePaymentAndChange: false,
  });
  const kitchenHtml = buildKitchenTicketsHtml(d);
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Proforma Order #${d.order.orderNo}</title><style>${SHARED_STYLES}</style></head><body>${proformaHtml}${kitchenHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

/**
 * พิมพ์ Supplementary Bill (เฉพาะรายการที่สั่งเพิ่ม) + kitchen tickets ของรายการเพิ่ม — ใช้เมื่อเพิ่มสินค้าเข้า Order ที่สั่งก่อนจ่ายทีหลัง
 */
export async function printSupplementaryBillAndKitchen(
  orderId: string,
  branchId: string,
  newOrderItemIds: string[],
): Promise<void> {
  if (newOrderItemIds.length === 0) return;
  const d = await fetchPrintData(orderId, branchId, {
    newOrderItemIds,
  });
  const supplementaryHtml = buildSupplementaryBillPageHtml(d);
  const kitchenHtml = buildKitchenTicketsHtml(d);
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Supplementary Order #${d.order.orderNo}</title><style>${SHARED_STYLES}</style></head><body>${supplementaryHtml}${kitchenHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

/**
 * พิมพ์เฉพาะใบแจ้งหนี้ (PROFORMA) — ใช้จากหน้ารายละเอียด Order (กดได้แม้ยังไม่จ่ายเงิน)
 */
export async function printProformaOnly(
  orderId: string,
  branchId: string,
): Promise<void> {
  const d = await fetchPrintData(orderId, branchId);
  const proformaHtml = buildReceiptPageHtml(d, {
    billTitle: "BILL PROFORMA",
    includePaymentAndChange: false,
  });
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Proforma Order #${d.order.orderNo}</title><style>${SHARED_STYLES}</style></head><body>${proformaHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

/**
 * พิมพ์เฉพาะใบเสร็จ (RECEIPT) — ใช้เมื่อกดจ่ายเงินจากหน้ารายละเอียด Order
 */
export async function printReceiptOnly(
  orderId: string,
  branchId: string,
): Promise<void> {
  const d = await fetchPrintData(orderId, branchId);
  const receiptHtml = buildReceiptPageHtml(d, {
    billTitle: "RECEIPT",
    includePaymentAndChange: true,
  });
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt Order #${d.order.orderNo}</title><style>${SHARED_STYLES}</style></head><body>${receiptHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}

/**
 * พิมพ์เฉพาะ kitchen tickets
 */
export async function printKitchenOnly(
  orderId: string,
  branchId: string,
): Promise<void> {
  const d = await fetchPrintData(orderId, branchId);
  const kitchenHtml = buildKitchenTicketsHtml(d);
  const combinedHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Kitchen Order #${d.order.orderNo}</title><style>${SHARED_STYLES}</style></head><body>${kitchenHtml}${WAIT_AND_PRINT_SCRIPT}</body></html>`;
  openPrintWindow(combinedHtml);
}
